import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react';
import AppPanel from '../fw/AppPanel.react';
import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';
import {injectIntl, defineMessages, FormattedMessage, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import {renderProgress} from './JobView.react';
import RefreshButton from './RefreshButton.react';
import * as Tool from '../fw/Tool';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { User } from '../../common/types/types';

export let durationColumnObject = {
	header: (
		<FormattedMessage defaultMessage="Duration" id="jobs.durationTitle" />
	),
	// eslint-disable-next-line max-params
	render: (column, item, url, columnMapping) => {
		if (!item.stop) {
			return '-';
		}
		let createdAtDate = new Date(item.createdAt);
		let endedAtDate = new Date(item.stop);

		let duration = endedAtDate - createdAtDate;
		let durationInSeconds = duration / 1000;

		if (durationInSeconds < 60) {
			let durationInSecondsRounded = Math.round(durationInSeconds, 10);

			if (durationInSeconds < 1) {
				durationInSecondsRounded = Math.round(durationInSeconds * 10, 10) / 10;
			}

			return (<FormattedMessage defaultMessage="{seconds} {seconds, plural, one {second} other {seconds}}" id="job.durationInSeconds" values={{seconds: durationInSecondsRounded}}/>);
		}

		let durationInMinutes = durationInSeconds / 60;
		let durationInMinutesRounded = Math.round(durationInMinutes, 10);

		return (<FormattedMessage defaultMessage="{minutes} {minutes, plural, one {minute} other {minutes}}" id="job.durationInMinutes" values={{minutes: durationInMinutesRounded}}/>);
	}
};

export function jobSort(jobA, jobB) {
	if (!jobA.node.createAtDate) {
		jobA.node.createAtDate = new Date(jobA.node.createdAt);
	}
	if (!jobB.node.createAtDate) {
		jobB.node.createAtDate = new Date(jobB.node.createdAt);
	}
	return jobB.node.createAtDate - jobA.node.createAtDate;
}

const messages = defineMessages({
    title: {id: 'jobList.title', 'defaultMessage': 'Jobs'}
});

interface UserJobListProps {
    intl: IntlShape;
    relay?: object;
    user: User;
}

class UserJobList extends React.Component<UserJobListProps> {

    typeName = 'jobs';

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired
    };

    getUrl = (item, column) => {
        if (column) {
            return;
        }

        return urlMapping.getUrl(types[this.typeName], 'user-view', {
            id: item.id,
        });
    };


    render() {
        let { user } = this.props;
        let list = user.jobs;
        let type = types[this.typeName];

		list.edges.sort(jobSort);

        return (
            <AppPanel breadcrumbParams={[
                {type: types['organisations'], data: this.props.user.organisation}
            ]} title={this.props.intl.formatMessage(messages.title)}>
                <div className="btn-toolbar" role="toolbar">
                    <RefreshButton autoRefresh relay={this.props.relay} />
                </div>

                <PagingGrid
                    columns={{
                        'name': {
							// eslint-disable-next-line max-params
                            render: (column, item, url, columnMapping) => {
                                let value = Tool.getFormatted(type, 'name', this.props.intl.formatMessage, item);

                                if (value) {
                                    return value;
                                } else {
                                    return Tool.getFormatted(type, 'type', this.props.intl.formatMessage, item);
                                }
                            }
                        },
                        'createdAt': {},
						'stop': durationColumnObject,
                        'status': {
							// eslint-disable-next-line max-params
                            render: (column, item, url, columnMapping) => {
                                return renderProgress(type, item, this.props.intl.formatMessage);
                            }
                        }
                    }}
                    edges={list}
                    getUrl={this.getUrl}
                    linkOnlyOnField={["name"]}
                    metadata={type}
                    pageInfo={list.pageInfo}
                    relay={this.props.relay}
                    urlMapping={urlMapping}
                />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(UserJobList), (props) => {
    return true;
}), {
    initialVariables: {
        organisationId: '',
        first: defaultPageSize,
        after: null,
        before: null,
        last: null,
    },
    prepareVariables (variables) {
        processPagingParams(variables);

		if (!variables.organisationId) {
			variables.organisationId = getLastOrganisationId();
		}

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                jobs(first: $first, after: $after, before: $before, last: $last) {
                    edges {
                        node {
                            id,
                            organisation_id,
                            type,
                            name,
                            status,
                            createdAt,
                            progress,
                            start,
                            stop,
                        }
                    },
                    pageInfo {
                        hasNextPage,
                        hasPreviousPage,
                        startCursor,
                        endCursor
                    }
                },
            }
        `,
    },
});
